import { useCallback, useEffect, useMemo, useState } from "react"

import { APIError } from "../../../../app/modules/auth"
import {
    GHLLocationTag,
    PassareBranchTagSettings,
    PassareOrganization,
    PassareTagSettings,
    PassareTagSettingsBundle,
    PassareTagSystemEntry,
    deletePassareLocationGHLTag,
    getPassareLocationGHLTags,
    getPassareOrganizationTagSettings,
    renamePassareLocationGHLTag,
    resetPassareOrganizationTagSettings,
    setPassareBranchTagSettings,
    setPassareLocationTagSettings,
    setPassareOrganizationTagSettings,
} from "../../../core/_requests"
import { KTCard, KTCardBody } from "../../../../_metronic/helpers"

const errorMessageOf = (exception: any, fallback: string) =>
    exception instanceof APIError ? exception.message : (exception?.response?.data?.error?.message || exception?.message || fallback)

// True when the saved rules are behaviorally the built-in defaults (a stored
// all-enabled/blank shape counts — it pushes the same tags as no rules at
// all). Drives the "Reset to defaults" button's disabled state.
const systemEntryAtDefault = (entry?: PassareTagSystemEntry) =>
    !entry || (entry.enabled !== false && (entry.value || '') === '')

const branchEntryAtDefault = (entry: PassareBranchTagSettings) =>
    entry.branch_name_enabled !== false &&
    (entry.branch_name_override || '') === '' &&
    (entry.custom_tags || []).length === 0

const bundleAtDefaults = (bundle: PassareTagSettingsBundle) => {
    const settings = bundle.tag_settings || {}
    return (
        systemEntryAtDefault(settings.mogul_bridge) &&
        systemEntryAtDefault(settings.passare) &&
        systemEntryAtDefault(settings.organization_name) &&
        (!settings.branch_name || settings.branch_name.enabled !== false) &&
        (settings.custom_tags || []).length === 0 &&
        Object.values(bundle.branch_tag_settings || {}).every(branchEntryAtDefault) &&
        bundle.locations.every(location => location.custom_tags.length === 0)
    )
}

// Small chip editor used for every custom-tag list (org, branch, location).
// Commas are not allowed inside a tag — the sync joins tags with "," — so
// typing one just splits into separate tags. Mirrors the backend sanitizer's
// caps (25 tags, 100 chars) so nothing is silently dropped at save time, and
// commits the typed draft on blur so text isn't lost by clicking Save.
const MAX_CUSTOM_TAGS = 25
const MAX_TAG_LENGTH = 100

function TagChipsEditor({
    tags,
    onChange,
    disabled = false,
    placeholder = 'Add a tag…',
}: {
    tags: string[]
    onChange: (next: string[]) => void
    disabled?: boolean
    placeholder?: string
}) {
    const [draft, setDraft] = useState('')
    const atCap = tags.length >= MAX_CUSTOM_TAGS

    const addDraft = () => {
        const pieces = draft.split(',').map(p => p.trim()).filter(p => p !== '')
        if (pieces.length === 0) return
        const existingLower = tags.map(t => t.toLowerCase())
        const additions = pieces.filter((p, i) =>
            !existingLower.includes(p.toLowerCase()) &&
            pieces.findIndex(x => x.toLowerCase() === p.toLowerCase()) === i
        ).slice(0, Math.max(0, MAX_CUSTOM_TAGS - tags.length))
        if (additions.length > 0) onChange([...tags, ...additions])
        setDraft('')
    }

    return (
        <div>
            <div className="d-flex flex-wrap gap-2 mb-2">
                {tags.length === 0 && <span className="text-muted fs-8">No custom tags</span>}
                {tags.map(tag => (
                    <span key={tag} className="badge badge-light-primary d-inline-flex align-items-center">
                        {tag}
                        {!disabled && (
                            <button
                                type="button"
                                className="btn btn-link p-0 ms-2 text-primary"
                                style={{ lineHeight: 1 }}
                                title={`Remove "${tag}"`}
                                onClick={() => onChange(tags.filter(t => t !== tag))}
                            >
                                <i className="bi bi-x fs-6"></i>
                            </button>
                        )}
                    </span>
                ))}
            </div>
            {!disabled && (
                <div className="d-flex gap-2 align-items-center">
                    <input
                        type="text"
                        className="form-control form-control-sm"
                        style={{ maxWidth: 220 }}
                        maxLength={MAX_TAG_LENGTH}
                        placeholder={placeholder}
                        value={draft}
                        disabled={atCap}
                        onChange={(e) => setDraft(e.target.value)}
                        onBlur={addDraft}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault()
                                addDraft()
                            }
                        }}
                    />
                    <button type="button" className="btn btn-sm btn-light" onClick={addDraft} disabled={draft.trim() === '' || atCap}>
                        Add
                    </button>
                    {atCap && <span className="text-muted fs-8">Tag limit reached ({MAX_CUSTOM_TAGS})</span>}
                </div>
            )}
        </div>
    )
}

// One built-in tag's rule row: on/off switch + optional rename.
function SystemTagRow({
    id,
    label,
    defaultValue,
    entry,
    onChange,
    warning,
}: {
    id: string
    label: string
    defaultValue: string
    entry: { enabled?: boolean; value?: string }
    onChange: (next: { enabled: boolean; value: string }) => void
    warning?: string
}) {
    const enabled = entry.enabled !== false
    const value = entry.value || ''

    return (
        <div className="d-flex flex-column flex-md-row align-items-md-center gap-3 py-3 border-bottom">
            <div className="form-check form-switch form-check-custom form-check-solid" style={{ minWidth: 260 }}>
                <input
                    className="form-check-input"
                    type="checkbox"
                    id={id}
                    checked={enabled}
                    onChange={(e) => onChange({ enabled: e.target.checked, value })}
                />
                <label className="form-check-label" htmlFor={id}>
                    <span className="fw-bold text-gray-800">{label}</span>
                </label>
            </div>
            <div className="flex-grow-1">
                <input
                    type="text"
                    className="form-control form-control-sm"
                    style={{ maxWidth: 320 }}
                    placeholder={defaultValue}
                    value={value}
                    disabled={!enabled}
                    onChange={(e) => onChange({ enabled, value: e.target.value })}
                />
                {warning && (
                    <div className="text-warning fs-9 mt-1">
                        <i className="bi bi-exclamation-triangle me-1"></i>{warning}
                    </div>
                )}
            </div>
        </div>
    )
}

const PREMIER_AUTOMATION_WARNING =
    "Premier's 360 automations may key on this tag — confirm with them before disabling or renaming it."

// Card 1: org-level rules for the four built-in tags plus org custom tags.
function OrgTagRulesCard({
    uuid,
    organizationName,
    settings,
    onSaved,
    onRequestReset,
    resetDisabled,
}: {
    uuid: string
    organizationName: string
    settings: PassareTagSettings
    onSaved: () => Promise<void>
    onRequestReset: () => void
    resetDisabled: boolean
}) {
    const [local, setLocal] = useState<PassareTagSettings>(settings)
    const [saving, setSaving] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [savedFlash, setSavedFlash] = useState(false)

    // Key on content, not object identity — a bundle refetch (e.g. after a
    // branch save below) must not wipe unsaved edits in this card.
    const settingsJson = JSON.stringify(settings)
    useEffect(() => {
        setLocal(JSON.parse(settingsJson))
    }, [settingsJson])

    const dirty = useMemo(() => JSON.stringify(local) !== JSON.stringify(settings), [local, settings])

    const handleSave = async () => {
        setSaving(true)
        setError(null)
        try {
            await setPassareOrganizationTagSettings(uuid, local)
            await onSaved()
            setSavedFlash(true)
            window.setTimeout(() => setSavedFlash(false), 2000)
        } catch (exception: any) {
            console.error(exception)
            setError(errorMessageOf(exception, 'Failed to save tag settings'))
        } finally {
            setSaving(false)
        }
    }

    return (
        <KTCard>
            <div className="card-header">
                <h3 className="card-title">Tags the bridge applies</h3>
                <div className="card-toolbar">
                    {savedFlash && <span className="text-success fs-7 me-3"><i className="bi bi-check-circle-fill me-1"></i>Saved</span>}
                    <button
                        className="btn btn-sm btn-light me-3"
                        onClick={onRequestReset}
                        disabled={saving || resetDisabled}
                        title={resetDisabled ? 'Every tag rule is already at its default' : 'Clear every tag rule on this page and restore the original tagging'}
                    >
                        <i className="bi bi-arrow-counterclockwise me-1"></i>Reset to defaults
                    </button>
                    <button className="btn btn-sm btn-primary" onClick={handleSave} disabled={saving || !dirty}>
                        {saving ? <><span className="spinner-border spinner-border-sm me-2" role="status"></span>Saving…</> : 'Save Tag Settings'}
                    </button>
                </div>
            </div>
            <KTCardBody>
                <div className="text-muted fs-7 mb-4">
                    Every lead this organization pushes to the 360 gets these tags. Rename a built-in tag by
                    typing a replacement (leave blank for the default), or switch it off entirely. Changes apply
                    to future pushes only — already-synced contacts keep the tags they have (manage those in the
                    "Tags in the 360" section below).
                </div>

                {error && <div className="alert alert-danger"><div className="alert-text">{error}</div></div>}

                <SystemTagRow
                    id="tagMogulBridge"
                    label='"Mogul Bridge" tag'
                    defaultValue="Mogul Bridge"
                    entry={local.mogul_bridge || {}}
                    onChange={(next) => setLocal(prev => ({ ...prev, mogul_bridge: next }))}
                    warning={PREMIER_AUTOMATION_WARNING}
                />
                <SystemTagRow
                    id="tagPassare"
                    label='"Passare" tag'
                    defaultValue="Passare"
                    entry={local.passare || {}}
                    onChange={(next) => setLocal(prev => ({ ...prev, passare: next }))}
                    warning={PREMIER_AUTOMATION_WARNING}
                />
                <SystemTagRow
                    id="tagOrgName"
                    label="Organization name tag"
                    defaultValue={organizationName}
                    entry={local.organization_name || {}}
                    onChange={(next) => setLocal(prev => ({ ...prev, organization_name: next }))}
                />

                <div className="d-flex align-items-center gap-3 py-3 border-bottom">
                    <div className="form-check form-switch form-check-custom form-check-solid" style={{ minWidth: 260 }}>
                        <input
                            className="form-check-input"
                            type="checkbox"
                            id="tagBranchName"
                            checked={local.branch_name?.enabled !== false}
                            onChange={(e) => setLocal(prev => ({ ...prev, branch_name: { enabled: e.target.checked } }))}
                        />
                        <label className="form-check-label" htmlFor="tagBranchName">
                            <span className="fw-bold text-gray-800">Branch name tag</span>
                        </label>
                    </div>
                    <div className="text-muted fs-8">
                        Tags each lead with its branch (e.g. "Gazaille Legacy Services"). Rename or switch it off
                        per branch in the table below.
                    </div>
                </div>

                <div className="pt-4">
                    <div className="fw-bold text-gray-800 mb-2">Custom tags (every lead from this organization)</div>
                    <TagChipsEditor
                        tags={local.custom_tags || []}
                        onChange={(next) => setLocal(prev => ({ ...prev, custom_tags: next }))}
                    />
                </div>
            </KTCardBody>
        </KTCard>
    )
}

// Card 2: per-branch rules (branch-name tag veto/override + branch custom tags).
function BranchTagRulesCard({
    uuid,
    bundle,
    onSaved,
}: {
    uuid: string
    bundle: PassareTagSettingsBundle
    onSaved: () => Promise<void>
}) {
    // localByBranch holds ONLY rows with in-progress edits (a delta over the
    // server state), so bundle refetches — including the one right after
    // saving a DIFFERENT row — can never wipe a sibling row's unsaved edits.
    // A row's delta is dropped when its own save succeeds; a tab-wide reset
    // clears all deltas via the epoch remount.
    const [localByBranch, setLocalByBranch] = useState<Record<string, PassareBranchTagSettings>>({})
    const [savingBranch, setSavingBranch] = useState<string | null>(null)
    const [savedBranch, setSavedBranch] = useState<string | null>(null)
    const [error, setError] = useState<string | null>(null)

    const serverFor = useCallback((branchUuid: string): PassareBranchTagSettings => (
        bundle.branch_tag_settings?.[branchUuid] || {}
    ), [bundle.branch_tag_settings])

    const localFor = (branchUuid: string): PassareBranchTagSettings => (
        localByBranch[branchUuid] || serverFor(branchUuid)
    )

    const isDirty = (branchUuid: string) => {
        const server = serverFor(branchUuid)
        const local = localFor(branchUuid)
        return (local.branch_name_enabled !== false) !== (server.branch_name_enabled !== false)
            || (local.branch_name_override || '') !== (server.branch_name_override || '')
            || JSON.stringify(local.custom_tags || []) !== JSON.stringify(server.custom_tags || [])
    }

    const updateLocal = (branchUuid: string, patch: Partial<PassareBranchTagSettings>) => {
        setLocalByBranch(prev => ({ ...prev, [branchUuid]: { ...localFor(branchUuid), ...patch } }))
    }

    const handleSave = async (branchUuid: string) => {
        setSavingBranch(branchUuid)
        setError(null)
        try {
            await setPassareBranchTagSettings(uuid, branchUuid, localFor(branchUuid))
            await onSaved()
            // The refetched server state now includes this save — drop only
            // this row's delta; other rows keep their unsaved edits.
            setLocalByBranch(prev => {
                const next = { ...prev }
                delete next[branchUuid]
                return next
            })
            setSavedBranch(branchUuid)
            window.setTimeout(() => setSavedBranch(curr => (curr === branchUuid ? null : curr)), 2000)
        } catch (exception: any) {
            console.error(exception)
            setError(errorMessageOf(exception, 'Failed to save branch tag settings'))
        } finally {
            setSavingBranch(null)
        }
    }

    if (bundle.branches.length === 0) {
        return null
    }

    const branchTagGloballyOff = bundle.tag_settings?.branch_name?.enabled === false

    return (
        <KTCard>
            <div className="card-header">
                <h3 className="card-title">Per-branch tags</h3>
            </div>
            <KTCardBody>
                <div className="text-muted fs-7 mb-4">
                    Fine-tune the branch-name tag per branch — rename it (e.g. "Gazaille Legacy Services" →
                    "Azle &amp; Lake Worth"), switch it off, or add custom tags that only this branch's leads get.
                </div>

                {branchTagGloballyOff && (
                    <div className="alert alert-warning d-flex align-items-center">
                        <i className="bi bi-exclamation-triangle-fill me-2"></i>
                        <span>The branch name tag is switched off for the whole organization (above), so per-branch
                        name settings here have no effect until it's re-enabled. Custom tags still apply.</span>
                    </div>
                )}

                {error && <div className="alert alert-danger"><div className="alert-text">{error}</div></div>}

                <div className="table-responsive">
                    <table className="table table-row-bordered align-middle">
                        <thead>
                            <tr className="fw-bold text-muted">
                                <th className="min-w-200px">Branch</th>
                                <th className="min-w-125px">Branch name tag</th>
                                <th className="min-w-200px">Tag override</th>
                                <th className="min-w-250px">Custom tags (this branch only)</th>
                                <th className="min-w-100px text-end">Save</th>
                            </tr>
                        </thead>
                        <tbody>
                            {bundle.branches.map(branch => {
                                const local = localFor(branch.branch_uuid)
                                const nameEnabled = local.branch_name_enabled !== false
                                return (
                                    <tr key={branch.branch_uuid}>
                                        <td>
                                            <div className="fw-bold">{branch.branch_name || '(No branch name)'}</div>
                                            <div className="text-muted fs-8">{branch.branch_uuid}</div>
                                        </td>
                                        <td>
                                            <div className="form-check form-switch form-check-custom form-check-solid">
                                                <input
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    checked={nameEnabled}
                                                    onChange={(e) => updateLocal(branch.branch_uuid, { branch_name_enabled: e.target.checked })}
                                                />
                                            </div>
                                        </td>
                                        <td>
                                            <input
                                                type="text"
                                                className="form-control form-control-sm"
                                                placeholder={branch.branch_name || 'Branch name'}
                                                value={local.branch_name_override || ''}
                                                disabled={!nameEnabled}
                                                onChange={(e) => updateLocal(branch.branch_uuid, { branch_name_override: e.target.value })}
                                            />
                                        </td>
                                        <td>
                                            <TagChipsEditor
                                                tags={local.custom_tags || []}
                                                onChange={(next) => updateLocal(branch.branch_uuid, { custom_tags: next })}
                                            />
                                        </td>
                                        <td className="text-end">
                                            {savedBranch === branch.branch_uuid ? (
                                                <span className="text-success fs-7"><i className="bi bi-check-circle-fill me-1"></i>Saved</span>
                                            ) : (
                                                <button
                                                    className="btn btn-sm btn-light-primary"
                                                    disabled={savingBranch !== null || !isDirty(branch.branch_uuid)}
                                                    onClick={() => handleSave(branch.branch_uuid)}
                                                >
                                                    {savingBranch === branch.branch_uuid
                                                        ? <><span className="spinner-border spinner-border-sm me-1" role="status"></span>…</>
                                                        : 'Save'}
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                )
                            })}
                        </tbody>
                    </table>
                </div>
            </KTCardBody>
        </KTCard>
    )
}

// Card 3: what a synced lead from each branch will actually be tagged with.
function TagPreviewCard({ bundle }: { bundle: PassareTagSettingsBundle }) {
    const statusBadge = (status: string) => {
        if (status === 'routed') return <span className="badge badge-light-success">Pushes</span>
        if (status === 'suppressed') return <span className="badge badge-light-warning" title="The nightly sync skips this branch; a manual Sync to 360 would push it with these tags.">Suppressed</span>
        return <span className="badge badge-light-danger">Not linked</span>
    }

    return (
        <KTCard>
            <div className="card-header">
                <h3 className="card-title">Effective tags preview</h3>
            </div>
            <KTCardBody>
                <div className="text-muted fs-7 mb-4">
                    Computed by the exact rules the sync uses — this is what a newly synced lead from each branch
                    will be tagged with. Suppressed branches show the tags a manual "Sync to 360" would apply.
                    The branch-name tag is shown from the branch's recorded name; each pushed lead uses its own
                    case's branch name (normally identical).
                </div>
                <div className="table-responsive">
                    <table className="table table-row-bordered align-middle">
                        <thead>
                            <tr className="fw-bold text-muted">
                                <th className="min-w-200px">Branch</th>
                                <th className="min-w-100px">Routing</th>
                                <th className="min-w-350px">Tags applied</th>
                            </tr>
                        </thead>
                        <tbody>
                            {bundle.preview.map(row => (
                                <tr key={row.branch_uuid || '__no_branch__'}>
                                    <td>
                                        <div className="fw-bold">{row.branch_name || (row.branch_uuid ? '(No branch name)' : '(No branch on the case)')}</div>
                                        {row.branch_uuid && <div className="text-muted fs-8">{row.branch_uuid}</div>}
                                    </td>
                                    <td>{statusBadge(row.status)}</td>
                                    <td>
                                        {row.effective_tags.length === 0 ? (
                                            <span className="text-muted fs-8">—</span>
                                        ) : (
                                            <div className="d-flex flex-wrap gap-2">
                                                {row.effective_tags.map(tag => (
                                                    <span key={tag} className="badge badge-light">{tag}</span>
                                                ))}
                                            </div>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </KTCardBody>
        </KTCard>
    )
}

// Card 4 (one per routed 360 location): location-scoped rules + the ACTUAL
// tags at that location right now, live from the 360, with rename/delete.
function LocationTagsCard({
    uuid,
    location,
    onRulesSaved,
}: {
    uuid: string
    location: { frm_location_id: string; sources: string[]; custom_tags: string[] }
    onRulesSaved: () => Promise<void>
}) {
    const [localRules, setLocalRules] = useState<string[]>(location.custom_tags)
    const [savingRules, setSavingRules] = useState(false)
    const [rulesSavedFlash, setRulesSavedFlash] = useState(false)
    const [rulesError, setRulesError] = useState<string | null>(null)

    const [liveTags, setLiveTags] = useState<GHLLocationTag[] | null>(null)
    const [loadingLive, setLoadingLive] = useState(false)
    const [liveError, setLiveError] = useState<string | null>(null)

    const [renamingTagId, setRenamingTagId] = useState<string | null>(null)
    const [renameDraft, setRenameDraft] = useState('')
    const [busyTagId, setBusyTagId] = useState<string | null>(null)
    const [deleteTarget, setDeleteTarget] = useState<GHLLocationTag | null>(null)
    const [deleting, setDeleting] = useState(false)

    const customTagsJson = JSON.stringify(location.custom_tags)
    useEffect(() => {
        setLocalRules(JSON.parse(customTagsJson))
    }, [customTagsJson])

    const rulesDirty = JSON.stringify(localRules) !== JSON.stringify(location.custom_tags)

    const handleSaveRules = async () => {
        setSavingRules(true)
        setRulesError(null)
        try {
            await setPassareLocationTagSettings(uuid, location.frm_location_id, localRules)
            await onRulesSaved()
            setRulesSavedFlash(true)
            window.setTimeout(() => setRulesSavedFlash(false), 2000)
        } catch (exception: any) {
            console.error(exception)
            setRulesError(errorMessageOf(exception, 'Failed to save location tags'))
        } finally {
            setSavingRules(false)
        }
    }

    const loadLiveTags = useCallback(async () => {
        setLoadingLive(true)
        setLiveError(null)
        try {
            const tags = await getPassareLocationGHLTags(uuid, location.frm_location_id)
            setLiveTags(tags)
        } catch (exception: any) {
            console.error(exception)
            setLiveError(errorMessageOf(exception, 'Failed to load tags from the 360'))
        } finally {
            setLoadingLive(false)
        }
    }, [uuid, location.frm_location_id])

    const handleRename = async (tag: GHLLocationTag) => {
        if (!tag.id) return
        const newName = renameDraft.trim()
        if (newName === '' || newName === tag.name) {
            setRenamingTagId(null)
            return
        }
        setBusyTagId(tag.id)
        setLiveError(null)
        try {
            await renamePassareLocationGHLTag(uuid, location.frm_location_id, tag.id, newName)
            setLiveTags(prev => (prev || []).map(t => (t.id === tag.id ? { ...t, name: newName } : t)))
            setRenamingTagId(null)
        } catch (exception: any) {
            console.error(exception)
            setLiveError(errorMessageOf(exception, 'Failed to rename the tag'))
        } finally {
            setBusyTagId(null)
        }
    }

    const handleDelete = async () => {
        if (!deleteTarget?.id) return
        setDeleting(true)
        setLiveError(null)
        try {
            await deletePassareLocationGHLTag(uuid, location.frm_location_id, deleteTarget.id)
            setLiveTags(prev => (prev || []).filter(t => t.id !== deleteTarget.id))
            setDeleteTarget(null)
        } catch (exception: any) {
            console.error(exception)
            setLiveError(errorMessageOf(exception, 'Failed to delete the tag'))
        } finally {
            setDeleting(false)
        }
    }

    return (
        <KTCard>
            <div className="card-header">
                <h3 className="card-title">
                    Tags in the 360 · <code className="ms-2 fs-7">{location.frm_location_id}</code>
                </h3>
                <div className="card-toolbar">
                    <span className="badge badge-light-info">{location.sources.join(' · ')}</span>
                </div>
            </div>
            <KTCardBody>
                {/* Rules strip: extra tags for anything the bridge pushes into this location */}
                <div className="mb-5 pb-4 border-bottom">
                    <div className="d-flex align-items-center justify-content-between mb-2">
                        <div className="fw-bold text-gray-800">Extra tags for anything pushed to this location</div>
                        <div>
                            {rulesSavedFlash && <span className="text-success fs-7 me-3"><i className="bi bi-check-circle-fill me-1"></i>Saved</span>}
                            <button className="btn btn-sm btn-light-primary" onClick={handleSaveRules} disabled={savingRules || !rulesDirty}>
                                {savingRules ? <><span className="spinner-border spinner-border-sm me-1" role="status"></span>Saving…</> : 'Save'}
                            </button>
                        </div>
                    </div>
                    <div className="text-muted fs-8 mb-3">
                        Applies to every lead the bridge pushes here, no matter which organization or branch it
                        came from (several branches can share one location).
                    </div>
                    {rulesError && <div className="alert alert-danger"><div className="alert-text">{rulesError}</div></div>}
                    <TagChipsEditor tags={localRules} onChange={setLocalRules} />
                </div>

                {/* Live tags straight from GHL */}
                <div className="d-flex align-items-center justify-content-between mb-3">
                    <div>
                        <div className="fw-bold text-gray-800">Actual tags at this location right now</div>
                        <div className="text-muted fs-8">
                            Live from the 360 — every tag that exists there, not just the ones the bridge applies.
                        </div>
                    </div>
                    <button className="btn btn-sm btn-light" onClick={loadLiveTags} disabled={loadingLive}>
                        {loadingLive
                            ? <><span className="spinner-border spinner-border-sm me-1" role="status"></span>Loading…</>
                            : <><i className="bi bi-arrow-clockwise me-1"></i>{liveTags === null ? 'Load live tags' : 'Refresh'}</>}
                    </button>
                </div>

                {liveError && <div className="alert alert-danger"><div className="alert-text">{liveError}</div></div>}

                {liveTags === null ? (
                    !loadingLive && (
                        <div className="text-muted fs-7">
                            Click "Load live tags" to fetch this location's tag list from the 360.
                        </div>
                    )
                ) : liveTags.length === 0 ? (
                    <div className="alert alert-info"><div className="alert-text">No tags exist at this location yet.</div></div>
                ) : (
                    <div className="table-responsive">
                        <table className="table table-row-bordered align-middle gs-0 gy-2">
                            <thead>
                                <tr className="fw-bold text-muted fs-7">
                                    <th className="min-w-300px">Tag</th>
                                    <th className="min-w-150px text-end">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {liveTags.map(tag => (
                                    <tr key={tag.id || tag.name || ''}>
                                        <td>
                                            {tag.id != null && renamingTagId === tag.id ? (
                                                <div className="d-flex gap-2 align-items-center">
                                                    <input
                                                        type="text"
                                                        className="form-control form-control-sm"
                                                        style={{ maxWidth: 280 }}
                                                        value={renameDraft}
                                                        autoFocus
                                                        onChange={(e) => setRenameDraft(e.target.value)}
                                                        onKeyDown={(e) => {
                                                            if (e.key === 'Enter') { e.preventDefault(); handleRename(tag) }
                                                            if (e.key === 'Escape') setRenamingTagId(null)
                                                        }}
                                                    />
                                                    <button className="btn btn-sm btn-primary" disabled={busyTagId === tag.id} onClick={() => handleRename(tag)}>
                                                        {busyTagId === tag.id ? <span className="spinner-border spinner-border-sm" role="status"></span> : 'Rename'}
                                                    </button>
                                                    <button className="btn btn-sm btn-light" onClick={() => setRenamingTagId(null)}>Cancel</button>
                                                </div>
                                            ) : (
                                                <span className="badge badge-light fs-7">{tag.name}</span>
                                            )}
                                        </td>
                                        <td className="text-end">
                                            <button
                                                className="btn btn-sm btn-light me-2"
                                                disabled={!tag.id || busyTagId !== null}
                                                onClick={() => { setRenamingTagId(tag.id); setRenameDraft(tag.name || '') }}
                                                title="Rename this tag in the 360"
                                            >
                                                <i className="bi bi-pencil me-1"></i>Rename
                                            </button>
                                            <button
                                                className="btn btn-sm btn-light-danger"
                                                disabled={!tag.id || busyTagId !== null}
                                                onClick={() => setDeleteTarget(tag)}
                                                title="Delete this tag from the 360"
                                            >
                                                <i className="bi bi-trash me-1"></i>Delete
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* Delete confirmation — writes into Premier's CRM, so be explicit. */}
                {deleteTarget && (
                    <>
                        <div className="modal-backdrop fade show" style={{ zIndex: 1040 }}></div>
                        <div className="modal fade show d-block" tabIndex={-1} style={{ zIndex: 1050 }}>
                            <div className="modal-dialog modal-dialog-centered">
                                <div className="modal-content" style={{ zIndex: 1060 }}>
                                    <div className="modal-header">
                                        <h5 className="modal-title">Delete tag from the 360?</h5>
                                        <button type="button" className="btn-close" onClick={() => setDeleteTarget(null)} disabled={deleting}></button>
                                    </div>
                                    <div className="modal-body">
                                        <p className="mb-3">
                                            <span className="badge badge-light fs-7 me-1">{deleteTarget.name}</span>
                                            will be deleted at location <code>{location.frm_location_id}</code>.
                                        </p>
                                        <div className="alert alert-warning mb-0">
                                            <div className="alert-text">
                                                This removes the tag from the location <strong>and from every contact currently
                                                carrying it</strong> in the 360. It cannot be undone from here.
                                            </div>
                                        </div>
                                        {liveError && (
                                            <div className="alert alert-danger mt-3 mb-0">
                                                <div className="alert-text">{liveError}</div>
                                            </div>
                                        )}
                                    </div>
                                    <div className="modal-footer">
                                        <button type="button" className="btn btn-light" onClick={() => setDeleteTarget(null)} disabled={deleting}>
                                            Cancel
                                        </button>
                                        <button type="button" className="btn btn-danger" onClick={handleDelete} disabled={deleting}>
                                            {deleting ? <><span className="spinner-border spinner-border-sm me-2" role="status"></span>Deleting…</> : 'Yes, delete tag'}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </KTCardBody>
        </KTCard>
    )
}

// The Tags tab: org rules → per-branch rules → preview → one live card per
// routed 360 location. All rules changes re-fetch the bundle so the preview
// always reflects what the sync will actually do. "Reset to defaults" is
// tab-wide: it clears every rule scope at once and remounts the rule cards
// (epoch key) so in-progress drafts are discarded too.
export default function PassareOrganizationTagsTab({
    passareOrganization,
}: {
    passareOrganization: PassareOrganization
}) {
    const uuid = passareOrganization.unique_identifier

    const [bundle, setBundle] = useState<PassareTagSettingsBundle | null>(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)

    const [epoch, setEpoch] = useState(0)
    const [showResetConfirm, setShowResetConfirm] = useState(false)
    const [resetting, setResetting] = useState(false)
    const [resetError, setResetError] = useState<string | null>(null)
    const [resetDone, setResetDone] = useState(false)

    const loadBundle = useCallback(async () => {
        try {
            const next = await getPassareOrganizationTagSettings(uuid)
            setBundle(next)
            setError(null)
        } catch (exception: any) {
            console.error(exception)
            setError(errorMessageOf(exception, 'Failed to load tag settings'))
        } finally {
            setLoading(false)
        }
    }, [uuid])

    useEffect(() => {
        loadBundle()
    }, [loadBundle])

    const atDefaults = useMemo(() => (bundle ? bundleAtDefaults(bundle) : true), [bundle])

    const handleReset = async () => {
        setResetting(true)
        setResetError(null)
        try {
            await resetPassareOrganizationTagSettings(uuid)
            await loadBundle()
            setEpoch(previous => previous + 1)
            setShowResetConfirm(false)
            setResetDone(true)
            window.setTimeout(() => setResetDone(false), 6000)
        } catch (exception: any) {
            console.error(exception)
            setResetError(errorMessageOf(exception, 'Failed to reset tag settings'))
        } finally {
            setResetting(false)
        }
    }

    if (loading) {
        return <div className="spinner-border" role="status"><span className="sr-only">Loading...</span></div>
    }

    // Full-screen error only when there is nothing to show. A refetch failure
    // AFTER a successful load must not unmount the cards (and their drafts) —
    // it renders as a banner over the last loaded state instead.
    if (!bundle) {
        return (
            <div>
                <div className="alert alert-danger" role="alert">{error || 'Failed to load tag settings'}</div>
                <button className="btn btn-primary" onClick={() => { setLoading(true); loadBundle() }}>Retry</button>
            </div>
        )
    }

    const locationsWithRules = bundle.locations.filter(location => location.custom_tags.length > 0)

    return (
        <>
            {error && (
                <div className="alert alert-warning d-flex align-items-center">
                    <i className="bi bi-exclamation-triangle-fill me-2"></i>
                    <span>Couldn't refresh tag settings ({error}) — showing the last loaded state. Saves that succeeded are still applied.</span>
                </div>
            )}
            {resetDone && (
                <div className="alert alert-success d-flex align-items-center">
                    <i className="bi bi-check-circle-fill me-2"></i>
                    <span>Tag rules reset to defaults — newly synced leads get the original tags again.</span>
                </div>
            )}
            <OrgTagRulesCard
                key={`org-rules-${epoch}`}
                uuid={uuid}
                organizationName={bundle.organization_name}
                settings={bundle.tag_settings}
                onSaved={loadBundle}
                onRequestReset={() => { setResetError(null); setShowResetConfirm(true) }}
                resetDisabled={atDefaults}
            />
            <BranchTagRulesCard key={`branch-rules-${epoch}`} uuid={uuid} bundle={bundle} onSaved={loadBundle} />
            <TagPreviewCard bundle={bundle} />
            {bundle.locations.length === 0 ? (
                <KTCard>
                    <div className="card-header"><h3 className="card-title">Tags in the 360</h3></div>
                    <KTCardBody>
                        <div className="alert alert-info">
                            <div className="alert-text">
                                No 360 locations are linked yet — link a default location or route a branch to a
                                location first, then its live tags can be managed here.
                            </div>
                        </div>
                    </KTCardBody>
                </KTCard>
            ) : (
                bundle.locations.map(location => (
                    <LocationTagsCard
                        key={`${location.frm_location_id}-${epoch}`}
                        uuid={uuid}
                        location={location}
                        onRulesSaved={loadBundle}
                    />
                ))
            )}

            {/* Reset confirmation — discards saved rules permanently, so spell
                out the scope and show exactly what tagging returns to. */}
            {showResetConfirm && (
                <>
                    <div className="modal-backdrop fade show" style={{ zIndex: 1040 }}></div>
                    <div className="modal fade show d-block" tabIndex={-1} style={{ zIndex: 1050 }}>
                        <div className="modal-dialog modal-dialog-centered">
                            <div className="modal-content" style={{ zIndex: 1060 }}>
                                <div className="modal-header">
                                    <h5 className="modal-title">Reset all tag rules to defaults?</h5>
                                    <button type="button" className="btn-close" onClick={() => setShowResetConfirm(false)} disabled={resetting}></button>
                                </div>
                                <div className="modal-body">
                                    <p>
                                        This clears every rule on this page and restores the original tagging —
                                        each newly synced lead gets:
                                    </p>
                                    <div className="d-flex flex-wrap gap-2 mb-4">
                                        <span className="badge badge-light">Mogul Bridge</span>
                                        <span className="badge badge-light">Passare</span>
                                        <span className="badge badge-light">{bundle.organization_name}</span>
                                        <span className="badge badge-light fst-italic">the lead's branch name</span>
                                    </div>
                                    <p className="mb-2 fw-bold text-gray-800">What gets cleared</p>
                                    <ul className="mb-4">
                                        <li>Renames and on/off switches for the built-in tags</li>
                                        <li>Custom tags for the organization and every branch</li>
                                        {locationsWithRules.length > 0 && (
                                            <li>
                                                Extra location tags at{' '}
                                                {locationsWithRules.map((location, index) => (
                                                    <span key={location.frm_location_id}>
                                                        {index > 0 && ', '}
                                                        <code>{location.frm_location_id}</code>
                                                    </span>
                                                ))}
                                                {' '}— those are shared, so they also stop applying to anything
                                                else the bridge pushes there
                                            </li>
                                        )}
                                    </ul>
                                    <div className="alert alert-info mb-0">
                                        <div className="alert-text">
                                            Only future pushes change. Contacts already in the 360 keep their
                                            tags, and the live tag list there is untouched.
                                        </div>
                                    </div>
                                    {resetError && (
                                        <div className="alert alert-danger mt-4 mb-0">
                                            <div className="alert-text">{resetError}</div>
                                        </div>
                                    )}
                                </div>
                                <div className="modal-footer">
                                    <button type="button" className="btn btn-light" onClick={() => setShowResetConfirm(false)} disabled={resetting}>
                                        Cancel
                                    </button>
                                    <button type="button" className="btn btn-danger" onClick={handleReset} disabled={resetting}>
                                        {resetting ? <><span className="spinner-border spinner-border-sm me-2" role="status"></span>Resetting…</> : 'Reset to defaults'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </>
    )
}
